import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileSignature, Users, Send, ArrowRight } from 'lucide-react'

const STORAGE_KEY = 'kobsign-onboarding-done'

export function useOnboarding() {
  const [show, setShow] = useState(() => !localStorage.getItem(STORAGE_KEY))

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1')
    setShow(false)
  }

  return { showOnboarding: show, dismissOnboarding: dismiss }
}

interface OnboardingSlidesProps {
  onDone: () => void
}

export function OnboardingSlides({ onDone }: OnboardingSlidesProps) {
  const { t } = useTranslation()
  const [current, setCurrent] = useState(0)
  const totalSlides = 3

  const next = () => {
    if (current < totalSlides - 1) {
      setCurrent(current + 1)
    } else {
      onDone()
    }
  }

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-950 dark:via-[rgb(var(--color-surface))] dark:to-[rgb(var(--color-surface))] overflow-hidden animate-fade-in">
      {/* Decorative bg */}
      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-primary-200/30 dark:bg-primary-800/15 blur-3xl" />
      <div className="absolute -bottom-16 -left-16 w-44 h-44 rounded-full bg-accent-200/25 dark:bg-accent-800/10 blur-3xl" />

      {/* Skip button */}
      {current < totalSlides - 1 && (
        <button
          onClick={onDone}
          className="absolute top-4 right-4 z-20 text-[11px] text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
        >
          {t('onboarding.skip')}
        </button>
      )}

      {/* Slide content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 relative z-10">
        {current === 0 && <Slide1 />}
        {current === 1 && <Slide2 />}
        {current === 2 && <Slide3 />}
      </div>

      {/* Bottom: dots + button */}
      <div className="relative z-10 px-6 pb-6 flex flex-col items-center gap-4">
        {/* Dots */}
        <div className="flex gap-2">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === current
                  ? 'w-6 bg-primary-500'
                  : 'w-1.5 bg-neutral-300 dark:bg-neutral-600'
              }`}
            />
          ))}
        </div>

        {/* Action button */}
        <button
          onClick={next}
          className="w-full px-4 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-md hover:shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
        >
          {current === totalSlides - 1 ? t('onboarding.getStarted') : t('common.next')}
          <ArrowRight size={15} />
        </button>
      </div>
    </div>
  )
}

function Slide1() {
  const { t } = useTranslation()
  return (
    <div className="text-center animate-fade-in">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-primary-500/20">
        <span className="text-2xl font-extrabold text-white">S</span>
      </div>
      <h2 className="text-[19px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100 mb-3">
        {t('onboarding.slide1Title')}
      </h2>
      <p className="text-[13px] text-neutral-500 dark:text-neutral-400 leading-relaxed max-w-[260px]">
        {t('onboarding.slide1Text')}
      </p>
    </div>
  )
}

function Slide2() {
  const { t } = useTranslation()

  const steps = [
    { icon: FileSignature, text: t('onboarding.slide2Step1') },
    { icon: Users, text: t('onboarding.slide2Step2') },
    { icon: Send, text: t('onboarding.slide2Step3') },
  ]

  return (
    <div className="text-center animate-fade-in">
      <h2 className="text-[19px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100 mb-6">
        {t('onboarding.slide2Title')}
      </h2>
      <div className="flex flex-col gap-4 w-full max-w-[240px]">
        {steps.map((step, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
              <step.icon size={18} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div className="text-left">
              <span className="text-[10px] font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wider">
                {i + 1}.
              </span>
              <p className="text-[13px] font-medium text-neutral-700 dark:text-neutral-300">
                {step.text}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Slide3() {
  const { t } = useTranslation()
  return (
    <div className="text-center animate-fade-in">
      <div className="w-14 h-14 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center mx-auto mb-5">
        <svg className="w-7 h-7 text-success-600 dark:text-success-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-[19px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100 mb-3">
        {t('onboarding.slide3Title')}
      </h2>
      <p className="text-[13px] text-neutral-500 dark:text-neutral-400 leading-relaxed max-w-[260px]">
        {t('onboarding.slide3Text')}
      </p>
    </div>
  )
}
